import { ChangeEvent, ReactElement, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as EmailValidator from "email-validator";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Heading } from "@twilio-paste/core/heading";
import { Option } from "@twilio-paste/core/select";

import CompositeCallout from "@twiliointernal/services-common/components/compositeCallout";
import CompositeInput from "@twiliointernal/services-common/components/compositeInput";
import CompositeInputFormPillGroup from "@twiliointernal/services-common/components/compositeInputFormPillGroup";
import CompositeSelect from "@twiliointernal/services-common/components/compositeSelect";
import CompositeTextArea from "@twiliointernal/services-common/components/compositeTextArea";
import { compositePillGroupTypes } from "@twiliointernal/services-common/components/compositePillGroup";

import {
  OneSupportCenterFormProps,
  OneSupportCenterUnprocessedFormValues,
  OneSupportCenterFormValues,
  OneSupportCenterFormValueType,
  ticketSubmissionViewCopy,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  findDuplicateStringValues,
  findDuplicateStringValuesFromArrays,
} from "@twiliointernal/e2-commons-shared-utils";

import { LegalStatement } from "./sub";
import { CompositeFileUploader } from "../../../../components";
import styles from "./OneSupportCenterGatedForm.module.css";

const OneSupportCenterGatedForm = ({
  calloutProps,
  loading,
  onSubmit,
  priorityLevels,
  userInput = "",
  comment = "",
  followUpQuestions = [],
  product = "",
  issueArea = "",
  showFollowUpQuestions = false,
}: OneSupportCenterFormProps): ReactElement => {
  const {
    handleSubmit: handleFormSubmit,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    getValues,
  } = useForm<OneSupportCenterFormValues>();

  /**
   * COLLABORATOR STATE
   */
  const [collaboratorErrorText, setCollaboratorErrorText] = useState("");
  const [collaboratorValue, updateCollaboratorValue] = useState<string>("");
  const [collaboratorViewType, setCollaboratorViewType] = useState(
    compositePillGroupTypes.CompositePillGroupViewType.form
  );

  /**
   * ATTACHMENTS HANDLERS
   *
   * We need a dedicated handle method for attachments because FileInput uses
   * a hidden <input /> element. This method takes the returned attachments from
   * FileInput and sets them to the appropriate form field in react-hook-form.
   * ___
   * setValue: https://react-hook-form.com/api/useform/setvalue
   * __
   * @param updatedAttachments File[]: Any array of files
   */
  const handleChangeAttachments = (
    newAttachments: File[],
    hasError: boolean
  ): void => {
    const existingAttachments = getValues("attachments") || [];

    setValue("attachments", [...existingAttachments, ...newAttachments]);

    if (hasError) {
      handleAttachmentsError();
    } else {
      clearErrors("attachments");
    }
  };

  const handleRemoveAttachment = (
    attachmentToRemove: File,
    hasError: boolean
  ): void => {
    const existingAttachments = getValues("attachments") || [];

    const updatedAttachments = existingAttachments.filter(
      (file) => file.name !== attachmentToRemove.name
    );

    setValue("attachments", updatedAttachments);

    if (hasError) {
      handleAttachmentsError();
    } else {
      clearErrors("attachments");
    }
  };

  const handleAttachmentsError = (): void => {
    setError("attachments", { type: "custom" });
  };

  /**
   * COLLABORATOR HANDLERS
   */
  const handleChangeCollaborator = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    updateCollaboratorValue(event.target.value);
  };

  const handleEditCollaborators = (): void => {
    setCollaboratorViewType(
      compositePillGroupTypes.CompositePillGroupViewType.form
    );
  };

  const handleSubmitCollaborators = (newCollaborators: string[]): void => {
    updateCollaboratorValue("");
    setCollaboratorErrorText("");
    setValue("collaborators", [
      ...(getValues("collaborators") || []),
      ...newCollaborators,
    ]);
    setCollaboratorViewType(
      compositePillGroupTypes.CompositePillGroupViewType.display
    );
  };

  const handleRemoveCollaborator = (newCollaborators: string[]): void => {
    setValue("collaborators", newCollaborators);
  };
  /**
   * Handler that runs during input validation and returns a boolean value indicating if the entered email is valid.
   */
  const validateEmail = (newCollaborators: string[]): boolean => {
    const isValid =
      !hasInvalidEmail(newCollaborators) &&
      !hasDuplicateEmails(newCollaborators);

    return isValid;
  };

  const hasInvalidEmail = (newCollaborators: string[]): boolean => {
    const hasFoundInvalidEmail = !!newCollaborators.find(
      (collaborator) => !EmailValidator.validate(collaborator)
    );

    if (hasFoundInvalidEmail) {
      setCollaboratorErrorText(
        ticketSubmissionViewCopy.oneSupportCenterForm.collaborators.errors
          .invalidEmail
      );
    }

    return hasFoundInvalidEmail;
  };

  const hasDuplicateEmails = (newCollaborators: string[]): boolean => {
    // Check if user entered duplicate emails in the input (checking against itself)
    const duplicateInputValues = findDuplicateStringValues(newCollaborators);

    if (duplicateInputValues.length) {
      setCollaboratorErrorText(
        ticketSubmissionViewCopy.oneSupportCenterForm.collaborators.errors.duplicateEmails(
          duplicateInputValues
        )
      );
    }

    // Check if user entered emails that has already been added (checking new input values against existing values)
    const addedCollaborators = getValues("collaborators") || [];
    const existingCollaborators = findDuplicateStringValuesFromArrays(
      addedCollaborators,
      newCollaborators
    );

    if (existingCollaborators.length) {
      setCollaboratorErrorText(
        ticketSubmissionViewCopy.oneSupportCenterForm.collaborators.errors.existingEmails(
          existingCollaborators
        )
      );
    }

    const hasDuplicates =
      !!duplicateInputValues.length || !!existingCollaborators.length;

    return hasDuplicates;
  };

  const handleSubmit: SubmitHandler<
    OneSupportCenterUnprocessedFormValues
  > = async (data: OneSupportCenterUnprocessedFormValues) => {
    await onSubmit(data);
  };

  // API defines which value of priority level should be considered default
  const defaultPriorityLevel =
    priorityLevels.find((priorityLevel) => priorityLevel.default) ||
    priorityLevels[0];

  return (
    <Box
      as="form"
      className={styles.oneSupportCenterFormContainer}
      onSubmit={handleFormSubmit(handleSubmit)}
    >
      <Heading as="h2" variant="heading30">
        {ticketSubmissionViewCopy.oneSupportCenterForm.header}
      </Heading>

      {calloutProps && <CompositeCallout {...calloutProps} />}

      <Controller
        name={OneSupportCenterFormValueType.subject}
        defaultValue={userInput}
        control={control}
        rules={{
          required: true,
        }}
        render={(field): ReactElement => (
          <CompositeInput
            disabled={loading}
            characterLimit={150}
            errorText={
              ticketSubmissionViewCopy.oneSupportCenterForm.subject.error
            }
            hasError={!!errors[OneSupportCenterFormValueType.subject]}
            id={OneSupportCenterFormValueType.subject}
            label={ticketSubmissionViewCopy.oneSupportCenterForm.subject.label}
            required
            type="text"
            {...field}
          />
        )}
      />

      <Controller
        name={OneSupportCenterFormValueType.collaborators}
        control={control}
        render={(field): ReactElement => (
          <CompositeInputFormPillGroup
            id={OneSupportCenterFormValueType.collaborators}
            label={
              ticketSubmissionViewCopy.oneSupportCenterForm.collaborators.label
            }
            errorText={collaboratorErrorText}
            emptyStateText={
              ticketSubmissionViewCopy.oneSupportCenterForm.collaborators.empty
            }
            helpText={
              ticketSubmissionViewCopy.oneSupportCenterForm.collaborators.help
            }
            viewType={collaboratorViewType}
            value={collaboratorValue}
            pills={field.value || []}
            required={false}
            onChange={handleChangeCollaborator}
            onValidate={validateEmail}
            onRemovePill={handleRemoveCollaborator}
            onSubmit={handleSubmitCollaborators}
            onEdit={handleEditCollaborators}
            hasError={!!collaboratorErrorText}
            marginBottom="space70"
          />
        )}
      />

      <Controller
        name={OneSupportCenterFormValueType.priorityLevel}
        control={control}
        defaultValue={defaultPriorityLevel?.value}
        rules={{
          required: true,
        }}
        render={(field): ReactElement => (
          <CompositeSelect
            disabled={loading}
            hasError={!!errors[OneSupportCenterFormValueType.priorityLevel]}
            id={OneSupportCenterFormValueType.priorityLevel}
            label={
              ticketSubmissionViewCopy.oneSupportCenterForm.priorityLevel.label
            }
            required
            {...field}
          >
            {priorityLevels.map(({ name, value }) => (
              <Option key={value} value={value}>
                {name}
              </Option>
            ))}
          </CompositeSelect>
        )}
      />

      <LegalStatement className={styles.legalStatement} />

      <Controller
        name={OneSupportCenterFormValueType.comment}
        defaultValue={comment}
        control={control}
        rules={{
          required: true,
        }}
        render={(field): ReactElement => (
          <CompositeTextArea
            disabled={loading}
            errorText={
              ticketSubmissionViewCopy.oneSupportCenterForm.comment.error
            }
            hasError={!!errors[OneSupportCenterFormValueType.comment]}
            helpText={
              ticketSubmissionViewCopy.oneSupportCenterForm.comment.helpText
            }
            id={OneSupportCenterFormValueType.comment}
            label={ticketSubmissionViewCopy.oneSupportCenterForm.comment.label}
            required
            {...field}
          />
        )}
      />

      {/* The hidden fields below exist so we can include product, issue area, and follow-up questions in the submitted form data */}
      <Controller
        name={`${OneSupportCenterFormValueType.product}`}
        control={control}
        rules={{
          required: false,
        }}
        defaultValue={product}
        render={(field): ReactElement => <input type="hidden" {...field} />}
      />

      <Controller
        name={`${OneSupportCenterFormValueType.issueArea}`}
        control={control}
        rules={{
          required: false,
        }}
        defaultValue={issueArea}
        render={(field): ReactElement => <input type="hidden" {...field} />}
      />

      {followUpQuestions.map((question, index) => (
        <Controller
          name={`${OneSupportCenterFormValueType.followUpQuestions}[${index}]`}
          control={control}
          rules={{
            required: false,
          }}
          key={index}
          defaultValue={question}
          render={(field): ReactElement => <input type="hidden" {...field} />}
        />
      ))}

      {followUpQuestions.map((question, index) =>
        showFollowUpQuestions ? (
          <Controller
            name={`${OneSupportCenterFormValueType.followUpAnswers}[${index}]`}
            control={control}
            rules={{
              required: false,
            }}
            key={index}
            defaultValue={""}
            render={(field): ReactElement => (
              <CompositeInput
                disabled={loading}
                errorText={
                  ticketSubmissionViewCopy.oneSupportCenterForm
                    .followUpQuestions.error
                }
                hasError={
                  !!errors[`${OneSupportCenterFormValueType.followUpData}`]
                }
                id={`${OneSupportCenterFormValueType.followUpAnswers}-${index}`}
                label={question}
                type="text"
                {...field}
              />
            )}
          />
        ) : null
      )}

      <Controller
        name={OneSupportCenterFormValueType.attachments}
        defaultValue={[]}
        control={control}
        render={(field): ReactElement => (
          <CompositeFileUploader
            id={OneSupportCenterFormValueType.attachments}
            disabled={loading}
            dropZoneHeight="7.5rem"
            files={field.value || []}
            marginBottom="space70"
            name={OneSupportCenterFormValueType.attachments}
            onChange={handleChangeAttachments}
            onRemove={handleRemoveAttachment}
          />
        )}
      />

      <Box as="div" className={styles.submitButtonContainer}>
        <Button variant="primary" type="submit" loading={loading}>
          {ticketSubmissionViewCopy.oneSupportCenterForm.submit.buttonValue}
        </Button>
      </Box>
    </Box>
  );
};

export default OneSupportCenterGatedForm;
